import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import api from '../services/api';

export type AudioTranscriptionStatus = 'idle' | 'recording' | 'transcribing' | 'error';

type BrowserSpeechRecognitionErrorEvent = Event & {
  error?: string,
};

type BrowserSpeechRecognitionAlternative = {
  transcript: string,
};

type BrowserSpeechRecognitionResult = {
  0: BrowserSpeechRecognitionAlternative,
  [index: number]: BrowserSpeechRecognitionAlternative,
  isFinal: boolean,
  length: number,
};

type BrowserSpeechRecognitionResultList = {
  0: BrowserSpeechRecognitionResult,
  [index: number]: BrowserSpeechRecognitionResult,
  length: number,
};

type BrowserSpeechRecognitionEvent = Event & {
  results: BrowserSpeechRecognitionResultList,
};

type BrowserSpeechRecognition = {
  continuous: boolean,
  interimResults: boolean,
  lang: string,
  onstart: (() => void) | null,
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null,
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null,
  onend: (() => void) | null,
  start: () => void,
  stop: () => void,
};

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor,
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor,
  }
}

type UseAudioTranscriptionOptions = {
  context: string,
  maxDurationMs?: number,
  onTranscription: (text: string) => void,
};

const DEFAULT_MAX_DURATION_MS = 60_000;
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
] as const;

const getSupportedMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }

  return PREFERRED_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? '';
};

const getFileExtension = (mimeType: string) => {
  if (mimeType.includes('wav')) {
    return 'wav';
  }

  if (mimeType.includes('mp4')) {
    return 'mp4';
  }

  if (mimeType.includes('ogg')) {
    return 'ogg';
  }

  return 'webm';
};

const getSpeechRecognitionConstructor = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const getReadableRecognitionError = (error?: string) => {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return '请允许麦克风权限后再试。';
    case 'audio-capture':
      return '当前设备没有可用的麦克风。';
    case 'network':
      return '当前浏览器语音识别网络异常，请稍后重试。';
    case 'no-speech':
      return '没有识别到语音，请靠近麦克风再试一次。';
    case 'aborted':
      return '';
    default:
      return '语音识别失败，请重试。';
  }
};

const getReadableError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || '语音转写失败，请重试。';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return '语音转写失败，请重试。';
};

export const useAudioTranscription = ({
  context,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
  onTranscription,
}: UseAudioTranscriptionOptions) => {
  const [status, setStatus] = useState<AudioTranscriptionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const speechRecognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<number | null>(null);
  const discardRecordingRef = useRef(false);
  const shouldStopAfterStartRef = useRef(false);
  const mimeType = useMemo(getSupportedMimeType, []);
  const speechRecognitionConstructor = useMemo(getSpeechRecognitionConstructor, []);
  const isSecureContextSupported = typeof window !== 'undefined' ? window.isSecureContext : false;
  const canUseMediaRecorder =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;
  const canUseSpeechRecognition = Boolean(speechRecognitionConstructor);
  const isSupported = isSecureContextSupported && (canUseSpeechRecognition || canUseMediaRecorder);

  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const resetRecorder = () => {
    mediaRecorderRef.current = null;
    speechRecognitionRef.current = null;
    chunksRef.current = [];
    discardRecordingRef.current = false;
    shouldStopAfterStartRef.current = false;
    clearTimeoutRef();
    stopTracks();
  };

  const transcribeBlob = useCallback(
    async (blob: Blob) => {
      const resolvedMimeType = blob.type || mimeType || 'audio/webm';
      const file = new File([blob], `speech.${getFileExtension(resolvedMimeType)}`, {
        type: resolvedMimeType,
      });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('context', context);

      setStatus('transcribing');
      setErrorMessage('');

      try {
        const response = await api.post(
          '/speech/transcriptions',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const nextText =
          typeof response.data?.text === 'string'
            ? response.data.text.trim()
            : '';

        if (nextText) {
          onTranscription(nextText);
        }

        setStatus('idle');
      } catch (error) {
        setErrorMessage(getReadableError(error));
        setStatus('error');
      }
    },
    [context, mimeType, onTranscription]
  );

  const stopRecording = useCallback(() => {
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      clearTimeoutRef();
      setStatus('transcribing');
      recognition.stop();
      return;
    }

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      shouldStopAfterStartRef.current = true;
      return;
    }

    recorder.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    discardRecordingRef.current = true;
    stopRecording();
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    if (!isSecureContextSupported) {
      setErrorMessage('麦克风仅支持在 HTTPS 域名下使用，请使用 wildbroccoli.online 打开。');
      setStatus('error');
      return;
    }

    if (!isSupported) {
      setErrorMessage('当前设备浏览器不支持录音，请改为手动输入。');
      setStatus('error');
      return;
    }

    if (status === 'recording' || status === 'transcribing') {
      return;
    }

    if (canUseMediaRecorder) {
      try {
        setErrorMessage('');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

        streamRef.current = stream;
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];
        discardRecordingRef.current = false;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        recorder.onstop = async () => {
          const shouldDiscard = discardRecordingRef.current;
          const recordedChunks = [...chunksRef.current];
          resetRecorder();

          if (shouldDiscard || recordedChunks.length === 0) {
            setStatus('idle');
            return;
          }

          const blob = new Blob(recordedChunks, { type: mimeType || recordedChunks[0].type || 'audio/webm' });
          await transcribeBlob(blob);
        };

        recorder.onerror = () => {
          setErrorMessage('录音失败，请重试。');
          setStatus('error');
          resetRecorder();
        };

        recorder.start();
        setStatus('recording');

        timeoutRef.current = window.setTimeout(() => {
          stopRecording();
        }, maxDurationMs);

        if (shouldStopAfterStartRef.current) {
          stopRecording();
        }
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          setErrorMessage('请允许麦克风权限后再试。');
        } else {
          setErrorMessage(getReadableError(error));
        }
        setStatus('error');
        resetRecorder();
        return;
      }
    }

    if (canUseSpeechRecognition && speechRecognitionConstructor) {
      try {
        setErrorMessage('');
        discardRecordingRef.current = false;

        const recognition = new speechRecognitionConstructor();
        speechRecognitionRef.current = recognition;
        recognition.lang = 'zh-CN';
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => {
          setStatus('recording');
        };

        recognition.onresult = (event) => {
          const transcript = Array.from({ length: event.results.length })
            .map((_, index) => event.results[index]?.[0]?.transcript || '')
            .join('')
            .trim();

          if (transcript && !discardRecordingRef.current) {
            onTranscription(transcript);
          }
        };

        recognition.onerror = (event) => {
          const message = getReadableRecognitionError(event.error);
          if (message) {
            setErrorMessage(message);
            setStatus('error');
          } else {
            setStatus('idle');
          }
        };

        recognition.onend = () => {
          speechRecognitionRef.current = null;
          clearTimeoutRef();
          if (discardRecordingRef.current) {
            setStatus('idle');
            discardRecordingRef.current = false;
            return;
          }

          setStatus((prev) => (prev === 'error' ? prev : 'idle'));
        };

        recognition.start();
        timeoutRef.current = window.setTimeout(() => {
          stopRecording();
        }, maxDurationMs);
        return;
      } catch (error) {
        setErrorMessage(getReadableError(error));
        setStatus('error');
        resetRecorder();
        return;
      }
    }

    setErrorMessage('当前设备浏览器不支持录音，请改为手动输入。');
    setStatus('error');
  }, [
    canUseMediaRecorder,
    canUseSpeechRecognition,
    isSupported,
    isSecureContextSupported,
    maxDurationMs,
    mimeType,
    onTranscription,
    speechRecognitionConstructor,
    status,
    stopRecording,
    transcribeBlob,
  ]);

  useEffect(() => () => {
    discardRecordingRef.current = true;
    speechRecognitionRef.current?.stop();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    resetRecorder();
  }, []);

  return {
    status,
    errorMessage,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};
